/**
 * This is a standalone component that renders a clickable behavior for the
 * Khanmigo activities link in the header. It wraps an icon with the text "Khanmigo".
 */

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useContext} from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {addStyle, type StyleType} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {srOnly} from "@khan/shared-styles";

import {BackgroundContext} from "./background-context.ts";

import caretDownIcon from "@phosphor-icons/core/fill/caret-down-fill.svg";
import caretUpIcon from "@phosphor-icons/core/fill/caret-up-fill.svg";

const StyledSvg = addStyle("svg");

const DefaultGuideIcon = ({style}: {style?: StyleType}): React.ReactElement => (
    <StyledSvg
        style={style}
        preserveAspectRatio="xMidYMid"
        viewBox="0 0 56 56"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g clipPath="url(#clip0_14874_72680)">
            <path
                d="M17.24 13.34H38.83C41.69 13.34 44.02 15.67 44.02 18.53V42.02C44.02 44.29 41.32 45.47 39.66 43.93L37.91 42.31C37.43 41.86 36.80 41.61 36.14 41.61H14.65C13.21 41.61 12.05 40.45 12.05 39.02V18.53C12.05 15.67 14.38 13.34 17.24 13.34Z"
                fill="#DEAE93"
            />
            <circle
                cx="6.23284"
                cy="6.23284"
                r="6.23284"
                transform="matrix(1 0 0 -1 14.95 35.084)"
                fill="white"
            />
            <mask
                id="mask0_14874_72680"
                style={{maskType: "alpha"}}
                maskUnits="userSpaceOnUse"
                x="14"
                y="21"
                width="14"
                height="15"
            >
                <ellipse
                    cx="6.66855"
                    cy="6.66852"
                    rx="6.66855"
                    ry="6.66852"
                    transform="matrix(1 0 0 -1 14.5143 35.084)"
                    fill="white"
                />
            </mask>
            <g mask="url(#mask0_14874_72680)">
                <ellipse
                    cx="6.05032"
                    cy="6.05029"
                    rx="6.05032"
                    ry="6.05029"
                    transform="matrix(1 0 0 -1 15.1349 40.6602)"
                    fill="black"
                />
            </g>
            <circle
                cx="6.23284"
                cy="6.23284"
                r="6.23284"
                transform="matrix(1 0 0 -1 28.58 35.084)"
                fill="white"
            />
            <mask
                id="mask1_14874_72680"
                style={{maskType: "alpha"}}
                maskUnits="userSpaceOnUse"
                x="28"
                y="21"
                width="14"
                height="15"
            >
                <ellipse
                    cx="6.66855"
                    cy="6.66852"
                    rx="6.66855"
                    ry="6.66852"
                    transform="matrix(1 0 0 -1 28.1443 35.084)"
                    fill="white"
                />
            </mask>
            <g mask="url(#mask1_14874_72680)">
                <ellipse
                    cx="6.05032"
                    cy="6.05029"
                    rx="6.05032"
                    ry="6.05029"
                    transform="matrix(1 0 0 -1 28.7649 40.6602)"
                    fill="black"
                />
            </g>
        </g>
        <defs>
            <clipPath id="clip0_14874_72680">
                <rect
                    width="32"
                    height="32"
                    fill="white"
                    transform="translate(12 13)"
                />
            </clipPath>
        </defs>
    </StyledSvg>
);

const KhanmigoActivitiesClickable = ({
    navItemStyles,
    isOpen,
    ...props
}: Omit<React.ComponentProps<typeof Clickable>, "children"> & {
    navItemStyles: StyleType;
    isOpen?: boolean; // isOpen prop will only be passed in when the clickable is a dropdown, NOT a link
}): React.ReactElement | null => {
    const isBackgroundWhite = useContext(BackgroundContext);
    return (
        <Clickable
            {...props}
            target={undefined}
            style={[navItemStyles, styles.group]}
            role="button"
            testId="khanmigo-clickable"
        >
            {() => (
                <>
                    <DefaultGuideIcon
                        style={
                            isOpen !== undefined
                                ? styles.khanmigoIconWithTriangle
                                : styles.khanmigoIconNoTriangle
                        }
                    />
                    <LabelMedium style={styles.hideTextBreakpoint}>
                        {t`Khanmigo`}
                    </LabelMedium>

                    {/* The caret icon will only appear when the clickable opens a dropdown */}
                    {isOpen !== undefined && (
                        <PhosphorIcon
                            style={[
                                styles.triangle,
                                !isBackgroundWhite
                                    ? styles.transparentTriangle
                                    : {},
                            ]}
                            icon={isOpen ? caretUpIcon : caretDownIcon}
                            size="small"
                        />
                    )}
                </>
            )}
        </Clickable>
    );
};

const styles = StyleSheet.create({
    group: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "right",
        height: "100%",
        boxShadow: "none",
        outline: "none",
    },
    khanmigoIconNoTriangle: {
        width: "32px",
        "@media screen and (max-width: 767px) /* mediaQueries.smOrSmaller */": {
            marginRight: spacing.xSmall_8,
        },
    },
    khanmigoIconWithTriangle: {
        width: "32px",
    },
    hideTextBreakpoint: {
        marginInlineStart: 5,
        fontWeight: "bold",
        "@media screen and (max-width: 1199px) /* mediaQueries.lgOrSmaller */":
            srOnly,
    },
    triangle: {
        marginLeft: spacing.xxSmall_6,
        position: "relative",
        "@media screen and (max-width: 1199px) /* mediaQueries.lgOrSmaller */":
            {
                marginLeft: 0,
            },
    },
    transparentTriangle: {
        color: "rgba(255, 255, 255, 0.48)",
    },
});

export default KhanmigoActivitiesClickable;
